import React, { useCallback, useEffect, useState } from 'react';
import {
  listThreats, getThreat, resolveThreat, markFalsePositive, blockThreatIp, exportThreatsCsv, errorMessage,
} from '../api/securityApi';
import SecurityNav from '../components/Security/SecurityNav';
import SiteSelector, { useActiveSite } from '../components/Security/SiteSelector';
import { SevBadge, ActionBadge } from '../components/Security/Charts';
import RequirePermission from '../auth/RequirePermission';

const empty = { severity: '', action: '', status: '', type: '', ip: '', search: '' };

export default function ThreatsPage() {
  const { sites, siteId, setSiteId } = useActiveSite();
  const [f, setF] = useState(empty);
  const [page, setPage] = useState(1);
  const [res, setRes] = useState({ items: [], totalCount: 0, pageSize: 25 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sel, setSel] = useState(null);
  const [busy, setBusy] = useState(false);

  const query = { ...f, siteId, page, pageSize: 25 };

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try { setRes(await listThreats({ ...f, siteId, page, pageSize: 25 })); }
    catch (e) { setError(errorMessage(e)); }
    finally { setLoading(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [f, siteId, page]);

  useEffect(() => { load(); }, [load]);

  const open = async (id) => {
    try { setSel(await getThreat(id)); } catch (e) { setError(errorMessage(e)); }
  };
  const act = async (fn) => {
    setBusy(true);
    try { const updated = await fn(); if (updated?.id) setSel(updated); await load(); }
    catch (e) { setError(errorMessage(e)); }
    finally { setBusy(false); }
  };
  const set = (k) => (e) => { setPage(1); setF({ ...f, [k]: e.target.value }); };
  const pages = Math.max(1, Math.ceil(res.totalCount / (res.pageSize || 25)));

  let evidence = '';
  if (sel) { try { evidence = JSON.stringify(JSON.parse(sel.evidence), null, 2); } catch { evidence = sel.evidence; } }

  return (
    <div className="shell">
      <header className="page-header"><div><h1>Threats</h1><p className="muted">Every flagged session, with the reasons behind the score.</p></div></header>
      <SecurityNav />
      <div className="sec-toolbar">
        <SiteSelector sites={sites} value={siteId} onChange={(v) => { setPage(1); setSiteId(v); }} />
        <select className="sec-select" value={f.severity} onChange={set('severity')}>
          <option value="">Any severity</option>{['Low', 'Medium', 'High', 'Critical'].map((x) => <option key={x}>{x}</option>)}</select>
        <select className="sec-select" value={f.action} onChange={set('action')}>
          <option value="">Any action</option>{['Allowed', 'Monitored', 'Blocked'].map((x) => <option key={x}>{x}</option>)}</select>
        <select className="sec-select" value={f.status} onChange={set('status')}>
          <option value="">Any status</option>{['Open', 'Resolved', 'FalsePositive'].map((x) => <option key={x}>{x}</option>)}</select>
        <input className="sec-input" placeholder="IP address" value={f.ip} onChange={set('ip')} />
        <input className="sec-input" placeholder="Search…" value={f.search} onChange={set('search')} />
        <button className="ghost" onClick={() => { setPage(1); setF(empty); }}>Clear</button>
        <RequirePermission code="ivt.export">
          <button className="ghost" onClick={() => exportThreatsCsv(query).catch((e) => setError(errorMessage(e)))}>Export CSV</button>
        </RequirePermission>
      </div>
      {error && <div className="toast error">{error}</div>}

      <div className="sec-card sec-scroll">
        <table className="sec-table">
          <thead><tr><th>Time</th><th>IP</th><th>Severity</th><th>Score</th><th>Action</th><th>Status</th><th>Reasons</th></tr></thead>
          <tbody>
            {res.items.map((t) => (
              <tr key={t.id} className="clickable" onClick={() => open(t.id)}>
                <td>{new Date(t.createdAt + (String(t.createdAt).endsWith('Z') ? '' : 'Z')).toLocaleString()}</td>
                <td className="sec-mono">{t.ipAddress}</td>
                <td><SevBadge value={t.severity} /></td>
                <td>{t.riskScore}</td>
                <td><ActionBadge value={t.actionTaken} /></td>
                <td>{t.status}</td>
                <td className="muted">{t.reasons.slice(0, 3).join(', ')}{t.reasons.length > 3 ? ` +${t.reasons.length - 3}` : ''}</td>
              </tr>
            ))}
            {!loading && res.items.length === 0 && <tr><td colSpan="7" className="muted">No threats match these filters.</td></tr>}
            {loading && <tr><td colSpan="7" className="muted">Loading…</td></tr>}
          </tbody>
        </table>
        <div className="sec-pager">
          <span className="muted">{res.totalCount} total</span>
          <button className="ghost" disabled={page <= 1} onClick={() => setPage(page - 1)}>Prev</button>
          <span>{page} / {pages}</span>
          <button className="ghost" disabled={page >= pages} onClick={() => setPage(page + 1)}>Next</button>
        </div>
      </div>

      {sel && (
        <div className="sec-drawer-bg" onClick={() => setSel(null)}>
          <aside className="sec-drawer" onClick={(e) => e.stopPropagation()}>
            <div className="sec-row" style={{ justifyContent: 'space-between' }}>
              <h2 style={{ margin: 0 }}>Threat detail</h2><button className="ghost" onClick={() => setSel(null)}>Close</button>
            </div>
            <p><SevBadge value={sel.severity} /> <ActionBadge value={sel.actionTaken} /> <b>score {sel.riskScore}</b> · {sel.status}</p>
            <p><b>IP</b> <span className="sec-mono">{sel.ipAddress}</span><br />
              <b>Site</b> {sel.siteKey}<br /><b>Type</b> {sel.threatType}<br />
              <b>Fingerprint</b> <span className="sec-mono">{sel.fingerprint}</span><br />
              <b>Session</b> <span className="sec-mono">{sel.sessionId}</span></p>
            <p><b>User agent</b><br /><span className="sec-mono">{sel.userAgent}</span></p>
            <p><b>Reasons</b><br />{sel.reasons.map((r) => <span key={r} className="sec-pill" style={{ marginRight: 6, display: 'inline-block', marginBottom: 6 }}>{r}</span>)}</p>
            <p><b>Evidence</b></p><pre>{evidence}</pre>
            <RequirePermission code="ivt.manage">
              <div className="sec-row">
                <button className="primary" disabled={busy || sel.status === 'Resolved'} onClick={() => act(() => resolveThreat(sel.id))}>Mark resolved</button>
                <button className="ghost" disabled={busy} onClick={() => act(() => markFalsePositive(sel.id, false))}>False positive</button>
                <button className="ghost" disabled={busy} onClick={() => act(() => markFalsePositive(sel.id, true))}>False positive + allowlist IP</button>
                <button className="ghost danger" disabled={busy} onClick={() => act(() => blockThreatIp(sel.id, 1440))}>Block IP (24h)</button>
                <button className="ghost danger" disabled={busy} onClick={() => act(() => blockThreatIp(sel.id))}>Block IP (permanent)</button>
              </div>
            </RequirePermission>
          </aside>
        </div>
      )}
    </div>
  );
}
